import { IPromise, IQService, IRootScopeService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { take } from "rxjs";
import colors from "wwwroot/json/colors.json";
import icons from "wwwroot/json/icons.json";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController } from "@gtmhub/core";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { ICroppedTeamPicture, ITeam, ITeamsIdMap, TeamService } from "@gtmhub/teams";
import { CfMap } from "@webapp/custom-fields/models/custom-fields.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { PeopleSelectorRequest } from "@webapp/shared/components/people-selector/models/models";
import { TeamAvatarModalData } from "@webapp/teams/models/add-team-avatar-form.models";
import { AddTeamAvatarModalService } from "@webapp/teams/services/add-team-avatar-modal.service";
import { UploadPictureService } from "@webapp/teams/services/upload-picture.service";
import { TeamEvents } from "../events";

interface ITeamFormStateParamsService {
  returnState: string;
  teamId: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "avatar-selector-only": boolean; // ajs router query params can only be dash-case
  parentTeamId?: string;
}

interface TeamsRedux {
  assigneesFetched: boolean;
  assigneesError: IUIError;
  assigneesIdMap: ITeamsIdMap;
}
export interface ITeamFormScope extends IModalScope, TeamsRedux {
  indicators: {
    loadingTeam?: IIndicator;
    saving?: IIndicator;
    showSuccessModal?: boolean;
  };

  isEdit: boolean;

  avatarSelector: {
    isOpen: boolean;
    showPreview: boolean;
    isCreateSymbol: boolean;
    setTabUploadPictureActive: boolean;
  };

  error: string;
  team: ITeam;
  teams: ITeam[];
  icons: string[];
  colors: string[];
  selectedParent: ITeam;
  requiredCfNotEmpty: boolean;

  imgData: {
    // this holds the blob with the cropped image
    croppedDataUrl: string;
    croppedDataForSend: string;
    pictureFile: File;
  };

  teamParentId: string[];
  teamManagerId: string[];
  teamAlreadyExists: boolean;
  checkIfTeamAlreadyExists(): void;
  cancel(): void;
  save(): void;
  setColor(color: string): void;
  setAvatar(avatar: string): void;
  setCroppedDataForSend(croppedData: string): void;
  toggleAvatarSelectorTabHeading(heading: string): void;
  updateCustomFields(customFields: CfMap): void;
  setRequiredCf(requiredCf: { areFilled: boolean }): void;
  selectParentTeamId(ids: string[]): void;
  selectManager(ids: string[]): void;
  selectMembers(ids: string[]): void;
  resetAvatar(): void;
  avatarSelectorApply(): void;
  avatarSelectorCancel(): void;
  handleCreateTeamAvatarFormRendering(): void;
  openMigratedCreateTeamAvatarModal(): void;
  avatarSelectorOnly: boolean;
  disableAvatarSelectorApplyBtn: boolean;
  peopleSelectorRequest: PeopleSelectorRequest;
  parentTeamSelectorRequest: PeopleSelectorRequest;
}
const bindStateToCtrl = (state: IAssigneesStoreState): TeamsRedux => {
  return {
    assigneesFetched: state.assignees.isFetched,
    assigneesError: state.assignees.error,
    assigneesIdMap: state.assignees.map,
  };
};

export class TeamFormCtrl extends GtmhubController {
  private teamEvents: TeamEvents;
  private isUploadMediaAvatarEnabled: boolean;
  private isMigratedCreateTeamAvatarFormEnabled: boolean;
  private migratedCreateTeamAvatarModalData: TeamAvatarModalData;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "TeamService",
    "$ngRedux",
    "$q",
    "AssigneeActions",
    "FeatureTogglesFacade",
    "AddTeamAvatarModalService",
    "UploadPictureService",
  ];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: ITeamFormScope & IExtendedRootScopeService,
    private $stateService: StateService,
    private $stateParams: ITeamFormStateParamsService,
    private teamService: TeamService,
    private $ngRedux: INgRedux,
    private $q: IQService,
    private assigneeActions: AssigneeActions,
    private featureTogglesFacade: FeatureTogglesFacade,
    private addTeamAvatarModalService: AddTeamAvatarModalService,
    private uploadPictureService: UploadPictureService
  ) {
    super();
    this.teamEvents = new TeamEvents(this.$scope);
    this.init();
    this.setPeopleSelectorsRequest();
  }

  private init = () => {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    this.$scope.indicators = { loadingTeam: { progress: true } };

    // extracted form a query param to persist the avatar selector only state on back navigation and reload
    this.$scope.avatarSelectorOnly = this.$stateParams["avatar-selector-only"];

    this.$scope.avatarSelector = {
      isOpen: this.$scope.avatarSelectorOnly,
      showPreview: false,
      isCreateSymbol: true,
      setTabUploadPictureActive: false,
    };

    this.$scope.imgData = {
      croppedDataUrl: "",
      croppedDataForSend: "",
      pictureFile: null,
    };
    this.$scope.colors = colors.avatarSelectorColors;
    this.$scope.icons = icons;

    this.$scope.cancel = this.cancel;
    this.$scope.save = this.save;
    this.$scope.setColor = this.setColor;
    this.$scope.setAvatar = this.setAvatar;
    this.$scope.toggleAvatarSelectorTabHeading = this.toggleAvatarSelectorTabHeading;
    this.$scope.setCroppedDataForSend = this.setCroppedDataForSend;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.setRequiredCf = this.setRequiredCf;
    this.$scope.selectParentTeamId = this.selectParentTeamId;
    this.$scope.selectManager = this.selectManager;
    this.$scope.selectMembers = this.selectMembers;
    this.$scope.resetAvatar = this.resetAvatar;
    this.$scope.handleCreateTeamAvatarFormRendering = this.handleCreateTeamAvatarFormRendering;
    this.$scope.openMigratedCreateTeamAvatarModal = this.openMigratedCreateTeamAvatarModal;
    this.$scope.checkIfTeamAlreadyExists = this.checkIfTeamAlreadyExists;
    this.$scope.teamAlreadyExists = false;
    this.$scope.avatarSelectorCancel = this.avatarSelectorCancel;
    this.$scope.avatarSelectorApply = this.avatarSelectorApply;
    this.$scope.disableAvatarSelectorApplyBtn = true;

    this.$scope.teamParentId = [];
    this.$scope.teamManagerId = [];

    this.onDestroy(this.$ngRedux.connect(bindStateToCtrl)(this.$scope));

    this.$q
      .all({
        isUploadMediaAvatarEnabled: this.featureTogglesFacade.isFeatureAvailable(FeatureFlag.UploadMediaAvatar),
        isMigratedCreateTeamAvatarFormEnabled: this.featureTogglesFacade.isFeatureAvailable(FeatureFlag.A14CreateTeamAvatarModal),
        fetchAssignees: this.$scope.watchUntilAndRejectOnError(
          () => this.$scope.assigneesFetched,
          () => this.$scope.assigneesError
        ),
      })
      .then(
        ({ isUploadMediaAvatarEnabled, isMigratedCreateTeamAvatarFormEnabled }) => {
          this.isUploadMediaAvatarEnabled = isUploadMediaAvatarEnabled;
          this.isMigratedCreateTeamAvatarFormEnabled = isMigratedCreateTeamAvatarFormEnabled;

          if (this.$stateParams.teamId) {
            this.$scope.isEdit = true;
            this.loadTeam();
          } else {
            this.$scope.isEdit = false;
            // to do: remove this TS cheat when refactoring
            // id is mandatory in ITeam so we need to create a new interface for a new team but this involves some refactoring
            this.$scope.team = <ITeam>{
              name: "",
              description: "",
              color: "#607293",
              avatar: "travel_world",
              customFields: {},
            };

            if (this.$stateParams.parentTeamId) {
              this.$scope.teamParentId.push(this.$stateParams.parentTeamId);
              this.selectParentTeamId(this.$scope.teamParentId);
            }
          }

          delete this.$scope.indicators.loadingTeam;
        },
        (error) => {
          this.$scope.indicators.loadingTeam = { error };
        }
      );
  };

  private handleCreateTeamAvatarFormRendering = (): void => {
    if (this.isMigratedCreateTeamAvatarFormEnabled) {
      return this.openMigratedCreateTeamAvatarModal();
    }

    this.$scope.avatarSelector.isOpen = !this.$scope.avatarSelector.isOpen;
  };

  private openMigratedCreateTeamAvatarModal = (): void => {
    const teamAvatarModalRef = this.addTeamAvatarModalService.openAddTeamAvatarModal(this.$scope.team);

    teamAvatarModalRef.afterClose.pipe(take(1)).subscribe((modalData: TeamAvatarModalData) => {
      if (modalData) {
        this.$scope.avatarSelector.showPreview = !this.$scope.avatarSelector.showPreview;

        if (modalData.imgData.pictureFile) {
          this.$scope.imgData.pictureFile = modalData.imgData.pictureFile;
          this.$scope.imgData.croppedDataForSend = modalData.imgData.pictureUrl;
          this.migratedCreateTeamAvatarModalData = modalData;
        }
      }
    });
  };

  private loadTeam = () => {
    this.$scope.team = this.$scope.assigneesIdMap[this.$stateParams.teamId];
    this.$scope.indicators.loadingTeam = this.$scope.team.parentId && { progress: true };
    this.$scope.team.customFields = this.$scope.team.customFields || {};

    if (this.$scope.team.picture) {
      this.toggleAvatarSelectorTabHeading("picture");
      this.$scope.avatarSelector.setTabUploadPictureActive = true;
      this.$scope.avatarSelector.showPreview = true;
    }

    if (this.$scope.team.avatar) {
      this.$scope.avatarSelector.showPreview = true;
    }

    window.setTimeoutOutsideAngular(() => document.getElementById("team-name-field")?.focus());
  };

  private setColor = (color) => {
    this.$scope.team.color = color;
    this.$scope.disableAvatarSelectorApplyBtn = false;
  };

  private setAvatar = (avatar) => {
    this.$scope.team.avatar = avatar;
    this.$scope.disableAvatarSelectorApplyBtn = false;
    // Remove custom image to update the avatar preview
    this.$scope.team.picture = "";
    this.$scope.imgData.pictureFile = null;
  };

  private setCroppedDataForSend = (croppedData: string): void => {
    // this method is to avoid a bug from library "ng-img-crop"
    if (this.$scope.avatarSelector.isOpen) {
      this.$scope.imgData.croppedDataForSend = croppedData;
    }
  };

  private toggleAvatarSelectorTabHeading = (heading: string) => {
    switch (heading) {
      case "symbol":
        this.$scope.avatarSelector.isCreateSymbol = true;
        this.$scope.imgData.pictureFile = null;
        break;
      case "picture":
        this.$scope.avatarSelector.isCreateSymbol = false;
        break;
    }
  };

  private selectParentTeamId = (ids: string[]): void => {
    this.$scope.teamParentId = ids;
    this.$scope.team.parentId = ids[0];
    this.$scope.$evalAsync();
  };

  private selectManager = (ids: string[]): void => {
    this.$scope.teamManagerId = ids;
    this.$scope.team.manager = ids[0];
    this.$scope.$evalAsync();
  };

  private selectMembers = (ids: string[]): void => {
    this.$scope.team.members = ids;
    this.$scope.$evalAsync();
  };

  private updateOrCreateTeam = (): void => {
    if (this.$stateParams.teamId) {
      this.updateTeam();
    } else {
      this.createTeam();
    }
  };

  private handleMigratedTeamAvatarUpload = (): void => {
    this.uploadPictureService.handlePictureUpload$(this.migratedCreateTeamAvatarModalData).subscribe({
      next: () => {
        this.updateOrCreateTeam();
      },
      error: (error) => {
        this.$scope.indicators.saving = { error };
      },
    });
  };

  private save = (): void => {
    this.$scope.indicators.saving = { progress: true };

    if (this.isMigratedCreateTeamAvatarFormEnabled && this.migratedCreateTeamAvatarModalData) {
      this.handleMigratedTeamAvatarUpload();
    } else {
      this.uploadPicture().then(
        () => this.updateOrCreateTeam(),
        (error) => (this.$scope.indicators.saving = { error })
      );
    }
  };

  private cancel = (): void => {
    const params = this.$stateParams.teamId ? { teamId: this.$stateParams.teamId } : {};
    this.$stateService.go("^.^", params);
  };

  private avatarSelectorCancel = (): void => {
    if (this.$scope.avatarSelectorOnly) {
      this.cancel();
    } else {
      this.$scope.avatarSelector.isOpen = !this.$scope.avatarSelector.isOpen;
    }
  };

  private avatarSelectorApply = (): void => {
    if (this.$scope.avatarSelectorOnly) {
      this.$scope.indicators.saving = { progress: true };

      this.uploadPicture()
        .then(() =>
          this.teamService.updateTeam({
            id: this.$scope.team.id,
            avatar: this.$scope.team.avatar,
            color: this.$scope.team.color,
            picture: this.$scope.team.picture,
          })
        )
        .then((team) => {
          this.teamEvents.broadcastTeamUpdated({ team });
          this.cancel();
        })
        .catch((error) => {
          this.$scope.indicators.saving = { error };
        });
    } else {
      this.$scope.avatarSelector.isOpen = !this.$scope.avatarSelector.isOpen;
      this.$scope.avatarSelector.showPreview = !this.$scope.avatarSelector.showPreview;
    }
  };

  private updateTeam = (): IPromise<void> => {
    this.$scope.indicators.saving = { progress: true };

    const payloadPartialTeam: ITeam = {
      id: this.$scope.team.id,
      avatar: this.$scope.team.avatar,
      color: this.$scope.team.color,
      picture: this.$scope.team.picture,
      description: this.$scope.team.description,
      name: this.$scope.team.name,
      parentId: this.$scope.team.parentId,
      customFields: this.$scope.team.customFields,
    };

    return this.teamService.updateTeam(payloadPartialTeam).then(
      (result) => {
        this.$scope.error = null;
        this.teamEvents.broadcastTeamUpdated({ team: result });

        delete this.$scope.indicators.saving;
        this.$scope.indicators.showSuccessModal = true;

        this.successModal();
      },
      (error) => {
        this.$scope.indicators.saving = { error };
      }
    );
  };

  private createTeam = () => {
    this.$scope.indicators.saving = { progress: true };

    this.teamService.createTeam(this.$scope.team).then(
      (result) => {
        this.$scope.error = null;
        this.$rootScope.$broadcast("assigneesChanged", true);

        delete this.$scope.indicators.saving;
        this.$scope.indicators.showSuccessModal = true;
        this.teamEvents.broadcastTeamCreated({ team: result });
        this.$scope.$dismiss();
      },

      (error) => {
        if (error.status === 409) {
          this.$scope.teamAlreadyExists = true;
          delete this.$scope.indicators.saving;
          return;
        }
        this.$scope.indicators.saving = { error };
      }
    );
  };

  private checkIfTeamAlreadyExists = (): void => {
    this.$scope.teamAlreadyExists = false;
  };

  private uploadPicture = (): IPromise<void> => {
    // if save the form on “Team symbol” the picture must be deleted
    if (this.$scope.avatarSelector.isCreateSymbol) {
      this.$scope.team.picture = "";
      return this.$q.resolve();

      // if there is a picture but changes something else in the form
    } else if (!this.$scope.avatarSelector.isCreateSymbol && !this.$scope.imgData.pictureFile) {
      return this.$q.resolve();
    } else {
      let uploadPromise: IPromise<string>;
      const picture: ICroppedTeamPicture = {
        data: this.$scope.imgData.croppedDataForSend,
        fileName: this.$scope.imgData.pictureFile.name,
      };

      if (this.isUploadMediaAvatarEnabled) {
        uploadPromise = this.teamService.uploadPictureV2(picture);
      } else {
        uploadPromise = this.teamService.uploadPicture(picture);
      }

      return uploadPromise.then((imgUrl: string) => {
        this.$scope.team.picture = imgUrl;
      });
    }
  };

  private resetAvatar = (): void => {
    this.$scope.team.picture = "";
    this.$scope.team.avatar = "travel_world";
    this.$scope.team.color = "#607293";
    this.$scope.imgData.pictureFile = null;
    this.$scope.avatarSelector.showPreview = !this.$scope.avatarSelector.showPreview;
  };

  private updateCustomFields = (customFields: CfMap): void => {
    this.$scope.team.customFields = customFields;
  };

  private setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.$scope.requiredCfNotEmpty = requiredCf.areFilled;

    this.$scope.$evalAsync();
  };

  private successModal = (): void => {
    const modalContent = angular.element(document.querySelector(".modal-content"));
    modalContent.css("width", "340px");
  };

  private setPeopleSelectorsRequest(): void {
    this.$scope.peopleSelectorRequest = {
      ...new PeopleSelectorRequest(),
      type: "user",
    };
    this.$scope.parentTeamSelectorRequest = {
      ...new PeopleSelectorRequest(),
      type: "team",
    };
  }
}
